import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { resolveApiUrl } from "../config/environment";
import type { SiteContentSettings } from "../entities/site-content";
import { CloseButton } from "./CloseButton";
import { SiteMediaUpload, type SiteContentServices } from "./SiteContentPages";
import { Notice } from "./Notice";

type Slide = NonNullable<SiteContentSettings["hero_slides"]>[number];
type DraftSlide = Omit<Slide, "image_url">;
type SlideEditor = { slide: DraftSlide; imageUrl: string | null };

function createSlideId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  const bytes = new Uint8Array(16);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const blankSlide = (): DraftSlide => ({
  id: createSlideId(),
  eyebrow: "Алтайэкопарк",
  title: "",
  description: "",
  href: "/",
  cta: "Подробнее",
  image_media_id: "",
});

function toInput(slides: readonly Slide[]): DraftSlide[] {
  return slides.map((slide) => {
    const { image_url, ...input } = slide;
    void image_url;

    return input;
  });
}

function SlideDialog({
  initial,
  initialImageUrl,
  services,
  onClose,
  onSave,
  pending,
}: {
  initial: DraftSlide;
  initialImageUrl: string | null;
  services: SiteContentServices;
  onClose: () => void;
  onSave: (slide: DraftSlide) => void;
  pending: boolean;
}) {
  const [slide, setSlide] = useState(initial);
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl);
  const update = <K extends keyof DraftSlide>(key: K, value: DraftSlide[K]) =>
    setSlide((current) => ({ ...current, [key]: value }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave(slide);
  };

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="slide-dialog-title"
    >
      <button
        className="modal-backdrop"
        type="button"
        aria-label="Закрыть редактор слайда"
        onClick={onClose}
      />
      <form className="modal-card site-slide-dialog" onSubmit={submit}>
        <header className="modal-title">
          <div>
            <p className="eyebrow">Главная страница</p>
            <h2 id="slide-dialog-title">
              {initial.title ? "Редактировать слайд" : "Новый слайд"}
            </h2>
          </div>
          <CloseButton label="Закрыть редактор слайда" onClick={onClose} />
        </header>
        <div className="site-form site-slide-dialog__form">
          <label>
            <span>Надзаголовок</span>
            <input
              required
              maxLength={80}
              value={slide.eyebrow}
              onChange={(event) => update("eyebrow", event.target.value)}
            />
          </label>
          <label>
            <span>Текст кнопки</span>
            <input
              required
              maxLength={80}
              value={slide.cta}
              onChange={(event) => update("cta", event.target.value)}
            />
          </label>
          <label className="site-form__wide">
            <span>Заголовок</span>
            <input
              required
              maxLength={255}
              value={slide.title}
              onChange={(event) => update("title", event.target.value)}
            />
          </label>
          <label className="site-form__wide">
            <span>Описание</span>
            <textarea
              required
              maxLength={1000}
              value={slide.description}
              onChange={(event) => update("description", event.target.value)}
            />
          </label>
          <label className="site-form__wide">
            <span>Ссылка кнопки</span>
            <input
              required
              maxLength={2000}
              value={slide.href}
              onChange={(event) => update("href", event.target.value)}
              placeholder="/news или https://…"
            />
          </label>
          <div className="site-form__wide slide-media-control">
            <span>Фоновое изображение</span>
            {imageUrl ? (
              <img src={imageUrl} alt="Выбранное изображение слайда" />
            ) : null}
            <SiteMediaUpload
              services={services}
              selectedMediaId={slide.image_media_id || undefined}
              onSelected={(media) => {
                update("image_media_id", media.id);
                setImageUrl(media.preview_url ? resolveApiUrl(media.preview_url) : media.url);
              }}
            />
          </div>
        </div>
        <footer className="modal-actions site-slide-dialog__actions">
          <button
            className="button button--primary"
            disabled={pending || !slide.image_media_id}
            type="submit"
          >
            Сохранить слайд
          </button>
          <button
            className="button button--secondary"
            disabled={pending}
            type="button"
            onClick={onClose}
          >
            Отмена
          </button>
        </footer>
      </form>
    </div>
  );
}

export function SiteSlidesPage({
  services,
}: {
  services: SiteContentServices;
}) {
  const client = useQueryClient();
  const [editor, setEditor] = useState<SlideEditor | null>(null);
  const settings = useQuery({
    queryKey: ["site", "settings"],
    queryFn: () => services.settings(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (slides: DraftSlide[]) => {
      const current = settings.data!.data;
      return services.updateSettings({
        settings: { ...current, hero_slides: slides },
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["site", "settings"] });
      setEditor(null);
    },
  });
  const slides = settings.data?.data.hero_slides ?? [];
  const persist = (next: DraftSlide[]) => save.mutate(next);

  return (
    <main
      className="content-page site-content-page"
      aria-labelledby="site-slides-title"
    >
      <header className="page-header page-heading--split">
        <div>
          <p className="eyebrow">Публичный сайт</p>
          <h1 id="site-slides-title">Слайды главной</h1>
          <p>
            Управляйте содержанием и порядком слайдов главной страницы.
            Изображения загружаются в защищённое хранилище.
          </p>
        </div>
        <button
          className="button button--primary"
          disabled={slides.length >= 8}
          type="button"
          onClick={() => setEditor({ slide: blankSlide(), imageUrl: null })}
        >
          Добавить слайд
        </button>
      </header>
      {settings.isLoading ? <p aria-busy="true">Загружаем слайды…</p> : null}
      {settings.isError || save.isError ? (
        <Notice>Не удалось сохранить слайды. Повторите попытку.</Notice>
      ) : null}
      {!settings.isLoading && slides.length === 0 ? (
        <section className="site-content-panel">
          <h2>Слайды ещё не добавлены</h2>
          <p>
            Добавьте первый слайд — он заменит встроенную демонстрационную
            подборку на главной странице.
          </p>
        </section>
      ) : null}
      <div className="site-slides-grid">
        {slides.map((slide, index) => (
          <article className="site-slide-card" key={slide.id}>
            {slide.image_url ? (
              <img src={resolveApiUrl(slide.image_url)} alt="" />
            ) : null}
            <div>
              <p className="eyebrow">Слайд {index + 1}</p>
              <h2>{slide.title}</h2>
              <p>{slide.description}</p>
              <div className="inline-actions">
                <button
                  className="button button--secondary button--compact"
                  type="button"
                  onClick={() =>
                    setEditor({
                      slide: toInput([slide])[0]!,
                      imageUrl: slide.image_url
                        ? resolveApiUrl(slide.image_url)
                        : null,
                    })
                  }
                >
                  Изменить
                </button>
                <button
                  className="button button--text"
                  disabled={index === 0 || save.isPending}
                  type="button"
                  onClick={() => {
                    const next = toInput(slides);
                    [next[index - 1], next[index]] = [
                      next[index]!,
                      next[index - 1]!,
                    ];
                    persist(next);
                  }}
                >
                  Выше
                </button>
                <button
                  className="button button--text"
                  disabled={index === slides.length - 1 || save.isPending}
                  type="button"
                  onClick={() => {
                    const next = toInput(slides);
                    [next[index], next[index + 1]] = [
                      next[index + 1]!,
                      next[index]!,
                    ];
                    persist(next);
                  }}
                >
                  Ниже
                </button>
                <button
                  className="button button--text button--danger"
                  disabled={save.isPending}
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Удалить слайд «${slide.title}»?`))
                      persist(
                        toInput(slides).filter((item) => item.id !== slide.id),
                      );
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {editor ? (
        <SlideDialog
          initial={editor.slide}
          initialImageUrl={editor.imageUrl}
          services={services}
          pending={save.isPending}
          onClose={() => setEditor(null)}
          onSave={(next) =>
            persist([
              ...toInput(slides).filter((item) => item.id !== next.id),
              next,
            ])
          }
        />
      ) : null}
    </main>
  );
}
